import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import CircularProgress from '@mui/material/CircularProgress';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Pagination from '@mui/material/Pagination';

function OtherDailySalesSummary() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalcgst, setTotalcgst] = useState(0);
    const [totalsgst, setTotalsgst] = useState(0);
    const [totaligst, setTotaligst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalpurchase, setTotalpurchase] = useState(0);
    const [totalqty, setTotalqty] = useState(0);

    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branchName, setBranchName] = useState(0);
    const [selectedBranches, setSelectedBranches] = useState([]); // Use an array for multiple selections
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(100); // Show 100 rows per page

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
        fetchCustomerData();
        fetchBranchData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const handleBranchSelect = (selectedOptions) => {
        setSelectedBranches(selectedOptions);
        const branchIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
        // You can now use branchIds to filter or send to the API
        setDataItem([]); // Clear the data when branches are changed
    };

    const fetchBranchData = () => {
        fetchData('ReportApi', `/Branch/${companyId}/${branchId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.Id,
                }));
                setBranchOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching branch data:', error);
            });
    };


    const [totalbills, setTotalbills] = useState(0);
    
    // Memoize filtered data to avoid recalculating on every render
    const filteredDataItem = useMemo(() => {
        return dataItem.filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [dataItem, searchQuery]);

    // Memoize totals calculation
    const totals = useMemo(() => {
        let totalcgst = 0;
        let totalsgst = 0;
        let totaligst = 0;
        let totalnet = 0;
        let totalamt = 0;
        let totalpurchase = 0;
        let qty = 0;
        let billNumbers = new Set();

        filteredDataItem.forEach(item => {
            totalcgst += item.CGSTAmount;
            totalsgst += item.SGSTAmount;
            totaligst += item.IGSTAmount;
            totalnet += item.TaxableAmount;
            totalamt += item.TotalAmount;
            totalpurchase += item.PurchaseAmount;
            qty += item.Quantity;
            billNumbers.add(item.BillNumber);
        });

        return {
            totalcgst,
            totalsgst,
            totaligst,
            totalnet,
            totalamt,
            totalpurchase,
            totalqty: qty,
            totalbills: billNumbers.size
        };
    }, [filteredDataItem]);

    // Update state when totals change
    useEffect(() => {
        setTotalcgst(totals.totalcgst);
        setTotalsgst(totals.totalsgst);
        setTotaligst(totals.totaligst);
        setTotalnet(totals.totalnet);
        setTotalamt(totals.totalamt);
        setTotalqty(totals.totalqty);
        setTotalpurchase(totals.totalpurchase);
        setTotalbills(totals.totalbills);
    }, [totals]);


    const fetchCustomerData = () => {

    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            let branchIds;

            if (selectedBranches.length === 0) {
                branchIds = branchOptions.map(branch => branch.value);
            } else {
                branchIds = selectedBranches.map(branch => branch.value);
            }
            try {
                const branchIdsString = branchIds.join(',');
                const response = await fetchData('OtherReportApi', `/DailySalesSummary/${companyId}/${branchIdsString}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Daily Sales Summary Details:', error);
            }
            finally {
                setLoading(false); // Hide loading screen
            }
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setPage(1); // Reset to first page when searching
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    // Paginated data
    const paginatedData = useMemo(() => {
        const startIndex = (page - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredDataItem.slice(startIndex, endIndex);
    }, [filteredDataItem, page, rowsPerPage]);

    const totalPages = Math.ceil(filteredDataItem.length / rowsPerPage);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    // const handleExportExcel = () => {
    //     const excelData = [];
    //     const headerCaption = [`Daily Sales Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
    //     excelData.push(headerCaption);
    //     const headerRow = ['Branch Name', 'Date', 'Time', 'Memo No', 'PG', 'IG', 'ISG', 'PF', 'Item No', 'Description', 'HSN Code', 'Quantity', 'MRP', 'MRP On Net', 'Discount %', 'Discount value', 'Additional Disc%', 'Add Disc Value', 'Cash Discount'
    //         , 'Balance', 'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value', 'Gift Voucher', 'Net Value + Tax', 'Purchase Rate', 'Total Purchase Value', 'Mode Of Payment', 'GST Number', 'Sales Man', 'Comments','GWP/PWP','Inter-City Stock Transfer', 'Category', 'Group', 'Season Code', 'Size Code'
    //     ];
    //     excelData.push(headerRow);
    //     filteredDataItem.forEach((rowData, index) => {
    //         const dataRow = [];
    //         dataRow.push(rowData.BranchName, formatDate(rowData.BillDate), rowData.BillTime, rowData.BillNumber, rowData.PG, rowData.IG, rowData.ISG, rowData.PF, parseFloat(rowData.Code), rowData.ItemName, rowData.HSNCode, rowData.Quantity, parseFloat(rowData.Rate.toFixed(2))
    //             , parseFloat(rowData.Amount.toFixed(2)), parseFloat(rowData.DiscountPercentage), parseFloat(rowData.DiscountAmount.toFixed(2)), parseFloat(rowData.AdditionalDiscountPercentage), parseFloat(rowData.AdditionalDiscountAmount.toFixed(2)), parseFloat(rowData.CashDiscount.toFixed(2)), parseFloat(rowData.TotalAmount.toFixed(2)), rowData.GSTRate, parseFloat(rowData.CGSTAmount.toFixed(2)), parseFloat(rowData.SGSTAmount.toFixed(2)), parseFloat(rowData.IGSTAmount.toFixed(2)), parseFloat(rowData.TaxableAmount.toFixed(2))
    //             , '', parseFloat(rowData.TotalAmount.toFixed(2)), parseFloat(rowData.PurchaseRate.toFixed(2)), parseFloat(rowData.PurchaseAmount.toFixed(2)), rowData.ModeOfPayment, rowData.GSTNumber, rowData.SalesManName, rowData.Remarks, rowData.Gwp,rowData.InterStockTransfer, rowData.CategoryName, rowData.ItemGroupName, rowData.SeasonCode, rowData.SizeCode);
    //         excelData.push(dataRow);
    //     });
    //     //, rowData.PGCategoryName
    //     // Add total row
    //     const totalRow = ['', '', '', '', '', '', '', '', '', 'Total Bills', totalbills, totalqty, '', '', '', '', '', '', totalamt, '', totalcgst, totalsgst, totaligst, totalnet, '', totalamt, '', totalpurchase, '', '', '', '', '', '','',''];
    //     excelData.push(totalRow);
    //     const ws = XLSX.utils.aoa_to_sheet(excelData);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, 'Daily Sales Summary');
    //     XLSX.writeFile(wb, 'DailySalesSummary.xlsx');
    // };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Daily Sales Summary');

        // Right-align numeric columns
        const rightAlignColumns = [
            'Quantity', 'MRP', 'MRP On Net', 'Discount %', 'Discount value',
            'Additional Disc%', 'Add Disc Value', 'Cash Discount', 'Balance',
            'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value',
            'Gift Voucher', 'Net Value + Tax', 'Purchase Rate', 'Total Purchase Value'
        ];

        // Header caption
        const headerCaption = `Daily Sales Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:AM1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' }; // Caption right-aligned

        // Header row
        const headerRowValues = [
            'Branch Name', 'Date', 'Time', 'Memo No', 'PG', 'IG', 'ISG', 'PF', 'Item No', 'Description', 'HSN Code', 'Quantity',
            'MRP', 'MRP On Net', 'Discount %', 'Discount value', 'Additional Disc%', 'Add Disc Value', 'Cash Discount',
            'Balance', 'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value', 'Gift Voucher', 'Net Value + Tax',
            'Mode Of Payment', 'GST Number', 'Sales Man', 'Comments', 'GWP/PWP',
            'Inter-City Stock Transfer', 'Category', 'Group', 'Season Code', 'Size Code'
        ];
        //'Purchase Rate', 'Total Purchase Value', 
        const headerRow = worksheet.addRow(headerRowValues);

        // Style header row
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFB6D7A8' } // Light green
            };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }; // Header left-aligned
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Fixed column widths
        worksheet.columns = headerRowValues.map(() => ({ width: 15 }));

        // Add data rows
        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.BranchName,
                formatDate(rowData.BillDate),
                rowData.BillTime,
                rowData.BillNumber,
                rowData.PG,
                rowData.IG,
                rowData.ISG,
                rowData.PF,
                parseFloat(rowData.Code),
                rowData.ItemName,
                rowData.HSNCode,
                rowData.Quantity,
                parseFloat(rowData.Rate.toFixed(2)),
                parseFloat(rowData.Amount.toFixed(2)),
                rowData.DiscountPercentage,
                parseFloat(rowData.DiscountAmount.toFixed(2)),
                rowData.AdditionalDiscountPercentage,
                parseFloat(rowData.AdditionalDiscountAmount.toFixed(2)),
                parseFloat(rowData.CashDiscount.toFixed(2)),
                parseFloat(rowData.TotalAmount.toFixed(2)),
                rowData.GSTRate,
                parseFloat(rowData.CGSTAmount.toFixed(2)),
                parseFloat(rowData.SGSTAmount.toFixed(2)),
                parseFloat(rowData.IGSTAmount.toFixed(2)),
                parseFloat(rowData.TaxableAmount.toFixed(2)),
                '',
                parseFloat(rowData.TotalAmount.toFixed(2)),

                rowData.ModeOfPayment,
                rowData.GSTNumber,
                rowData.SalesManName,
                rowData.Remarks,
                rowData.Gwp,
                rowData.InterStockTransfer,
                rowData.CategoryName,
                rowData.ItemGroupName,
                rowData.SeasonCode,
                rowData.SizeCode
            ];
            // parseFloat(rowData.PurchaseRate.toFixed(2)),
            // parseFloat(rowData.PurchaseAmount.toFixed(2)),

            const row = worksheet.addRow(dataRow);

            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });

        // Total row
        const totalRowValues = [
            '', '', '', '', '', '', '', '', '', 'Total Bills',
            totalbills, totalqty, '', '', '', '', '', '', '', totalamt, '',
            totalcgst, totalsgst, totaligst, totalnet, '', totalamt,//, '', totalpurchase,
            '', '', '', '', '', '', '', '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFF2CC' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Save Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'DailySalesSummary.xlsx');
    };



    const branchOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...branchOptions];

    return (
        <div className="w-full  mx-auto" style={{ width: '100%', marginTop: '10px' }}>
            <div style={{ width: '100%', marginBottom: '2', display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    <h6 className="block uppercase tracking-wide text-black productHead flex-grow linkcolor" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', color: 'black', width: '250px' }}>
                        Daily Sales Summary
                    </h6>
                </div>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px', marginLeft: '10px' }}>
                    {loading && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 9999
                        }}>
                            <div>
                                <CircularProgress style={{ color: 'white' }} />
                                <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                            </div>
                        </div>
                    )}
                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '10px' }}
                        onClick={fetchTableData}
                        className="linkcolor"
                    >
                        <IconButton onClick={fetchTableData}>
                            <RefreshIcon style={{ color: 'brown' }} />
                        </IconButton>
                        <span style={{ color: 'brown', fontWeight: 'bolder' }}>Refresh</span>
                    </Button>
                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '-10px' }}
                        onClick={handleExportExcel}
                        className="linkcolor"
                    >
                        <IconButton onClick={handleExportExcel}>
                            <FeedIcon style={{ color: 'darkgreen' }} />
                        </IconButton>
                        <span style={{ color: 'darkgreen', fontWeight: 'bolder' }}>Excel</span>
                    </Button>
                    <div style={{ marginBottom: '10px', marginLeft: '20px', display: 'flex', alignItems: 'center' }}>
                        <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                        {showSearchInput && (
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                                style={{ marginLeft: '10px', width: '275px' }}
                            />
                        )}
                    </div>
                </div>

                <div className="watermark">
                    <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '50px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ width: '100%', maxWidth: '30%', marginLeft: '10px', paddingRight: '5', marginRight: '10px', marginBottom: '2', flexBasis: 'auto' }}>

                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        Branch
                        <span style={{ color: 'red' }}>*</span>
                    </label>

                    <Select
                        options={branchOptionsWithBlank}
                        value={selectedBranches}
                        onChange={handleBranchSelect}
                        placeholder="Select a Branch"
                        isMulti
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: '30px',
                                fontSize: '13px',
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: '13px',
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: '30px',
                            }),
                        }}
                    />
                </div>
            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '550px', overflowY: 'auto', marginTop: '7px' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#08396B', Width: '2500px' }}>
                            <TableRow sx={{ whiteSpace: 'nowrap' }}>
                                <TableCell sx={{ backgroundColor: '#08396B', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Store</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Memo No</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>PG</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>IG</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>ISG</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>PF</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Item No</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Description</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>HSN Code</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Quantity</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>MRP</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>MRP On Net</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Discount %</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Discount value</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Additional Disc%</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Add Disc Value</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Cash Discount</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Balance</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>GST %</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>CGST Amount</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>SGST Amount</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>IGST Amount</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Net Value</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Gift Voucher</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Net Value + Tax</TableCell>
                                {/* <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Purchase Rate</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Total Purchase Value</TableCell> */}
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Mode Of Payment</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>GST Number</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Sales Man</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Comments</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>GWP/PWP</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Inter-City Stock Transfer</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Category</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Group</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Season Code</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase', textAlign: 'center' }}>Size Code</TableCell>

                            </TableRow>
                        </TableHead>

                        <TableBody sx={{ backgroundColor: 'whitesmoke' }}>
                            {paginatedData.map((data, index) => (
                                <TableRow
                                    key={data.id}
                                    className={data.routeName === 'Total' ? 'highlighted-row' : ''}
                                    sx={{
                                        padding: '7px',
                                        height: '52px',

                                        backgroundColor: data.routeName === 'Total' ? 'teal' : 'transparent',

                                    }}
                                >
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.BranchName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{formatDate(data.BillDate)} {data.BillTime}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.BillNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.PG}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.IG}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.ISG}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.PF}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.Code}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.ItemName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.HSNCode}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.Quantity}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.Rate.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.Amount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.DiscountPercentage}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.DiscountAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.AdditionalDiscountPercentage}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.AdditionalDiscountAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.CashDiscount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.TotalAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.GSTRate}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.CGSTAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.SGSTAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.IGSTAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.TaxableAmount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}></TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.TotalAmount.toFixed(2)}</TableCell>
                                    {/* <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.PurchaseRate.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.PurchaseAmount.toFixed(2)}</TableCell> */}
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.ModeOfPayment}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.GSTNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.SalesManName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.Remarks}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.Gwp}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.InterStockTransfer}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.CategoryName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.ItemGroupName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.SeasonCode}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.SizeCode}</TableCell>
                                </TableRow>
                            ))}

                            <TableRow
                                className={'highlighted-row'}
                                sx={{
                                    padding: '7px',
                                    height: '40px',

                                    backgroundColor: 'teal',

                                }}
                            >
                                <TableCell colSpan={8} />
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px', textTransform: 'uppercase' }}>Total Bills</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalbills}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalqty}</TableCell>
                                <TableCell colSpan={7} />
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalamt.toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}></TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalcgst.toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalsgst.toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totaligst.toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalnet.toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}></TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalamt.toFixed(2)}</TableCell>
                                {/* <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}></TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px' }}>{totalpurchase.toFixed(2)}</TableCell> */}
                                <TableCell colSpan={10} />
                            </TableRow>

                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            
            {/* Pagination Controls */}
            {filteredDataItem.length > rowsPerPage && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '20px' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                        Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, filteredDataItem.length)} of {filteredDataItem.length} records
                    </span>
                    <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </div>
            )}
        </div>
    );
}

export default OtherDailySalesSummary;
